import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CalendarClock, Lock, Newspaper, Scale, Activity } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { LiveXauusdPrice } from "@/components/site/LiveXauusdPrice";
import { MarketRegimeShowcase } from "@/components/site/MarketRegimeShowcase";
import { MacroToolsStrip } from "@/components/site/MacroToolsStrip";
import { getDeskIntel } from "@/lib/desk-intel.functions";
import { getMarketStats } from "@/lib/market-stats.functions";
import { getMarketStation, type MarketStation } from "@/lib/market-station.functions";
import { getGoldEconomicCalendar, type CalendarEvent } from "@/lib/economic-calendar.functions";
import { getGoldCot } from "@/lib/cot.functions";
import { getUsCpi } from "@/lib/cpi.functions";
import { getUsEmployment } from "@/lib/nfp.functions";
import { cpiSummary, nfpSummary } from "@/lib/macro-report-summary";
import { getActiveSession } from "@/components/dashboard/desk/MarketPulseBar";
import { cn } from "@/lib/utils";
import { LiveGoldChart } from "@/components/site/LiveGoldChart";
import { MonthlyPerformanceTrajectory } from "@/components/site/MonthlyPerformanceTrajectory";
import record from "@/data/track-record.json";

const REC = record.combined.overall;

const SITE = "https://xauproacademy.com";
const PATH = "/xauusd-analysis-today";

const FAQ = [
  {
    q: "Where can I see a live XAUUSD chart and spot price?",
    a: "This page shows the live XAUUSD spot price and an interactive gold chart with M5, M15, H1, H4 and daily timeframes, next to session context, pivot levels and the macro calendar. Access is free and needs no account.",
  },
  {
    q: "Is the XAU Pro gold track record verified?",
    a: `Every published desk trade on XAUUSD from January 2024 to October 2026 is logged in the public Performance Record: ${REC.trades} trades, ${REC.winRate}% win rate, profit factor ${REC.profitFactor} and ${REC.netR >= 0 ? "+" : ""}${REC.netR}R net, including every losing trade and the maximum drawdown of ${REC.maxDrawdownR}R. Past performance does not guarantee future results.`,
  },
  {
    q: "What moves XAUUSD today?",
    a: "Gold trades on three layers: the US dollar and real yields, high impact US macro releases such as CPI, NFP and FOMC decisions, and intraday liquidity around the London and New York sessions. The pulse above covers all three: live price and session context, the upcoming event calendar, and the latest official macro readings.",
  },
  {
    q: "Is this XAUUSD forecast a trading signal?",
    a: "No. This page publishes market context: price performance, public pivot levels, macro data and event risk. It carries no entry, stop or target. Qualified trade decisions with defined invalidation are published inside the Signals Desk for account holders.",
  },
  {
    q: "How often is this gold analysis updated?",
    a: "The spot price refreshes continuously during market hours. Session statistics and pivot levels recalculate with each completed trading day. Macro positioning (COT) updates weekly after the Friday CFTC release, and CPI and payrolls update with each official monthly release.",
  },
  {
    q: "Why combine technical levels with macro data?",
    a: "A level without context is only a number. The same support level behaves differently ahead of a CPI release than on a quiet session. Reading structure, liquidity and the macro calendar together is the institutional approach, and it is how the Orion engine qualifies every published setup.",
  },
  {
    q: "What is the best session to trade gold?",
    a: "London and New York carry the deepest liquidity, and the overlap between them produces the largest directional moves. The Asian session is typically quieter and more prone to false expansion. Gold frequently sweeps a session high or low before committing to direction, which is why session context matters more than any single indicator.",
  },
];

export const Route = createFileRoute("/xauusd-analysis-today")({
  head: () => ({
    meta: [
      { title: "XAUUSD Live Chart and Gold Analysis Today | XAU Pro Academy" },
      {
        name: "description",
        content:
          "Free XAUUSD live chart and gold spot price with today's market analysis: session context, pivot levels, high impact US events, COT, CPI and payrolls, plus a 3 year verified gold track record.",
      },
      {
        name: "keywords",
        content:
          "xauusd live chart, gold live chart, gold price live, gold price today, gold price live analysis, xauusd analysis today, gold technical analysis today, xauusd technical analysis, xauusd forecast, gold forecast, gold price forecast today, xauusd news today, xauusd live, gold market analysis",
      },
      { property: "og:title", content: "XAUUSD Live Chart, Gold Price & Today's Market Analysis" },
      {
        property: "og:description",
        content:
          "Live gold chart and spot price, session context, pivot levels, event risk and macro positioning, backed by a 3 year verified XAUUSD track record.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "XAUUSD Analysis Today: Live Gold Technical & Macro Pulse",
          description:
            "Live XAUUSD price, daily performance, pivot levels, session context, economic event risk and official US macro positioning for gold.",
          url: `${SITE}${PATH}`,
          about: { "@type": "FinancialProduct", name: "Gold Spot (XAUUSD)" },
          publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "XAUUSD Analysis Today", item: `${SITE}${PATH}` },
          ],
        }),
      },
    ],
  }),
  loader: async () => {
    const [stats, station, calendar, cot, cpi, nfp, desk] = await Promise.all([
      getMarketStats(),
      getMarketStation(),
      getGoldEconomicCalendar(),
      getGoldCot(),
      getUsCpi(),
      getUsEmployment(),
      getDeskIntel().catch(() => null),
    ]);
    return { stats, station, calendar, cot, cpi, nfp, desk };
  },
  staleTime: 5 * 60 * 1000,
  component: XauusdAnalysisTodayPage,
});

const pct = (x: number | null, digits = 2) =>
  x == null ? "n/a" : `${x > 0 ? "+" : ""}${x.toFixed(digits)}%`;

function XauusdAnalysisTodayPage() {
  const { stats, station, calendar, cot, cpi, nfp, desk } = Route.useLoaderData();

  const session = getActiveSession(new Date());
  const dailyChange = stats.snapshot.dailyChangePct;
  const cpiMonths = cpi.months;
  const latestCpi = cpiMonths[cpiMonths.length - 1];
  const prevCpi = cpiMonths[cpiMonths.length - 2];
  const nfpMonths = nfp.months;
  const latestNfp = nfpMonths[nfpMonths.length - 1];
  const prevNfp = nfpMonths[nfpMonths.length - 2];
  const cotWeeks = cot.weeks;
  const latestCot = cotWeeks[cotWeeks.length - 1];

  const cpiLine = latestCpi ? cpiSummary(latestCpi, prevCpi)[0]?.text : null;
  const nfpLine = latestNfp ? nfpSummary(latestNfp, prevNfp, null)[0]?.text : null;

  return (
    <Layout>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container relative mx-auto max-w-6xl px-4 pb-12 pt-16 md:pt-24">
          <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            Live Market Pulse · Updated Continuously
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold tracking-tight md:text-5xl">
            XAUUSD Live Chart: <span className="text-gradient-gold">Gold Price & Today's Analysis</span>
          </h1>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            One institutional read on gold: live price, session statistics, daily pivot levels, upcoming high impact
            US releases and official macro positioning. Context first, execution second.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <LiveXauusdPrice />
            <div className="inline-flex items-center gap-3 rounded-xl border border-border/60 bg-card/70 px-4 py-3">
              <div className="flex flex-col">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  Active session
                </span>
                <span
                  className={cn(
                    "mt-0.5 font-display text-2xl font-bold",
                    session !== "Closed" ? "text-emerald" : "text-foreground",
                  )}
                >
                  {session === "Closed" ? "Market Closed" : session}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span
                  className={cn(
                    "font-mono text-sm",
                    dailyChange != null && dailyChange > 0
                      ? "text-emerald"
                      : dailyChange != null && dailyChange < 0
                        ? "text-destructive"
                        : "text-muted-foreground",
                  )}
                >
                  {pct(dailyChange)}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Daily change
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Desk Reading: automated live state */}
      {desk ? (
        <section className="container mx-auto max-w-6xl px-4 pt-10">
          <div className="rounded-2xl border border-gold/30 bg-card/70 p-5 md:p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Desk Reading · Live</span>
              <span
                className={cn(
                  "rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em]",
                  desk.reading.bias === "bull"
                    ? "border-emerald/40 text-emerald"
                    : desk.reading.bias === "bear"
                      ? "border-destructive/40 text-destructive"
                      : "border-border text-muted-foreground",
                )}
              >
                {desk.reading.bias === "bull" ? "Buy side pressure" : desk.reading.bias === "bear" ? "Sell side pressure" : "Balanced"}
              </span>
            </div>
            <p className="mt-3 font-display text-lg leading-snug text-foreground md:text-xl">{desk.reading.state}</p>
            <p className="mt-1 text-sm text-muted-foreground">{desk.reading.warning}</p>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Generated automatically from live structure and liquidity. Context, not a trade signal.
            </p>
          </div>
        </section>
      ) : null}

      {/* Live chart */}
      <section className="container mx-auto max-w-6xl px-4 pt-10">
        <h2 className="mb-4 font-display text-2xl font-bold">Live XAUUSD chart</h2>
        <LiveGoldChart />
      </section>


      {/* Performance strip */}
      <section className="container mx-auto max-w-6xl px-4 py-10">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          <Activity className="h-3.5 w-3.5 text-gold" />
          Gold price performance by horizon
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {station.performance.map((row) => (
            <div key={row.label} className="rounded-xl border border-border/60 bg-card/60 p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{row.label}</div>
              <div
                className={cn(
                  "mt-2 font-mono text-xl font-semibold",
                  row.changePct != null && row.changePct > 0
                    ? "text-emerald"
                    : row.changePct != null && row.changePct < 0
                      ? "text-destructive"
                      : "text-foreground",
                )}
              >
                {pct(row.changePct)}
              </div>
              <div className="mt-1 font-mono text-[11px] text-muted-foreground">
                {row.changeUsd != null ? `${row.changeUsd > 0 ? "+" : row.changeUsd < 0 ? "-" : ""}$${Math.abs(row.changeUsd).toFixed(2)}` : "n/a"}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <MiniStat
            label="Average daily range (5D)"
            value={stats.avgDailyRange5d != null ? `$${stats.avgDailyRange5d.toFixed(2)}` : "n/a"}
          />
          <MiniStat
            label="Weekly change"
            value={pct(stats.weekly.changePct)}
            tone={
              stats.weekly.changePct != null && stats.weekly.changePct > 0
                ? "text-emerald"
                : stats.weekly.changePct != null && stats.weekly.changePct < 0
                  ? "text-destructive"
                  : undefined
            }
          />
          <MiniStat
            label="Previous session range"
            value={
              stats.prevDay.high != null && stats.prevDay.low != null
                ? `$${stats.prevDay.low.toFixed(2)} to $${stats.prevDay.high.toFixed(2)}`
                : "n/a"
            }
          />
        </div>
      </section>

      {/* Pivot levels */}
      <section className="container mx-auto max-w-6xl px-4 pb-10">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          <Scale className="h-3.5 w-3.5 text-gold" />
          Daily reference levels
          {station.reference.date && (
            <span className="normal-case tracking-normal">· derived from the {station.reference.date} session</span>
          )}
        </div>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <PivotTable title="Camarilla levels" levels={station.camarilla} />
          <PivotTable title="Fibonacci pivots" levels={station.fibonacci} />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Public arithmetic derived from the previous completed session. These are reference coordinates, not trade
          recommendations.
        </p>
      </section>
      {/* Regime showcase: three market states */}
      <MarketRegimeShowcase />

      {/* Verified track record */}
      <section className="container mx-auto max-w-6xl px-4 pt-12">
        <div className="rounded-2xl border border-gold/30 bg-card/70 p-6 md:p-8">
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            Verified record · Jan 2024 to Sep 2026
          </span>
          <h2 className="mt-3 font-display text-2xl font-bold md:text-3xl">
            3 years of documented XAUUSD trades
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            Every desk trade on gold is logged, including every loss. The chart reads the same audited record
            published on the Performance Record page.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5">
            <MiniStat label="Trades" value={String(REC.trades)} />
            <MiniStat label="Net result" value={`+${REC.netR}R`} tone="text-emerald" />
            <MiniStat label="Win rate" value={`${REC.winRate}%`} />
            <MiniStat label="Profit factor" value={String(REC.profitFactor)} />
            <MiniStat label="Max drawdown" value={`${REC.maxDrawdownR}R`} />
          </div>
          <div className="mt-6">
            <MonthlyPerformanceTrajectory compact />
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button asChild variant="gold">
              <Link to="/signals">
                Open a free account <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/track-record">View every trade</Link>
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Results in R at fixed risk per trade. Past performance does not guarantee future results.
          </p>
        </div>
      </section>

      {/* Event risk + macro pulse */}
      <section className="border-y border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                <CalendarClock className="h-3.5 w-3.5 text-gold" />
                Upcoming high impact US events
              </div>
              <div className="mt-4 space-y-2">
                {calendar.events.length === 0 ? (
                  <p className="rounded-xl border border-border/60 bg-card/60 p-4 text-sm text-muted-foreground">
                    No high impact gold relevant releases are scheduled in the current calendar window.
                  </p>
                ) : (
                  calendar.events.slice(0, 5).map((e) => <EventRow key={e.id} event={e} />)
                )}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Execution around these releases carries elevated spread and slippage risk. The desk applies a
                protection window around high impact prints.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                <Newspaper className="h-3.5 w-3.5 text-gold" />
                Official macro readings
              </div>
              <div className="mt-4 space-y-3">
                <MacroCard
                  title="US inflation (CPI)"
                  line={cpiLine}
                  href="/gold-inflation-cpi"
                  linkLabel="Full CPI and gold report"
                />
                <MacroCard
                  title="US employment (NFP)"
                  line={nfpLine}
                  href="/gold-nfp-employment"
                  linkLabel="Full payrolls and gold report"
                />
                <MacroCard
                  title="Institutional positioning (COT)"
                  line={
                    latestCot && cot.mmIndex3y != null
                      ? `Managed Money net ${latestCot.mmNet >= 0 ? "long" : "short"} ${Math.abs(latestCot.mmNet).toLocaleString("en-US")} contracts. The 3 year COT Index reads ${cot.mmIndex3y.toFixed(0)} of 100.`
                      : null
                  }
                  href="/gold-cot-report"
                  linkLabel="Full COT positioning report"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conversion: locked execution layer */}
      <section className="container mx-auto max-w-6xl px-4 py-14">
        <div className="overflow-hidden rounded-2xl border border-gold/40 bg-gradient-to-br from-gold-muted/40 via-card to-emerald-muted/30 p-7 md:p-10">
          <div className="grid items-center gap-8 md:grid-cols-2">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
                The execution layer
              </div>
              <h2 className="mt-3 font-display text-2xl font-bold md:text-3xl">
                Context is public. Qualified execution is inside the desk.
              </h2>
              <p className="mt-3 text-sm text-muted-foreground">
                This page tells you what the market is doing. The Signals Desk tells you what we are doing about it:
                graded setups with defined invalidation, first target and runner logic, published with browser alerts
                the moment a decision is made. Free account, no card required.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button asChild variant="gold">
                  <Link to="/signals">
                    Create your free account <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/track-record">Review the audited record</Link>
                </Button>
              </div>
            </div>
            <div className="relative rounded-xl border border-border/60 bg-background/60 p-5">
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-background/60 px-6 text-center">
                <Lock className="h-6 w-6 text-gold" />
                <span className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  Entry, invalidation and targets are member level data
                </span>
              </div>
              <div className="space-y-3 opacity-60">
                {["Directional bias (H4 regime)", "Liquidity sweep confirmation", "Entry zone and invalidation", "First target and runner plan"].map(
                  (label) => (
                    <div key={label} className="flex items-center justify-between rounded-lg border border-border/40 px-4 py-3">
                      <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
                      <span className="font-mono text-xs text-gold">••••••</span>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-4xl px-4 py-14">
          <h2 className="font-display text-2xl font-bold">XAUUSD analysis: common questions</h2>
          <div className="mt-6 space-y-5">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="font-semibold">{f.q}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-4 text-sm">
            <Link to="/gold-analysis" className="text-gold hover:underline">Gold market analysis hub</Link>
            <Link to="/orion" className="text-gold hover:underline">How the Orion engine works</Link>
            <Link to="/education" className="text-gold hover:underline">Education hub</Link>
            <Link to="/case-studies" className="text-gold hover:underline">Trade case studies</Link>
            <Link to="/position-size-calculator" className="text-gold hover:underline">Gold position size calculator</Link>
            <Link to="/gold-trading-sessions" className="text-gold hover:underline">Gold trading hours clock</Link>
            <Link to="/prop-firm-gold" className="text-gold hover:underline">Prop firm gold trading guide</Link>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            For educational and research purposes only. Not investment advice. Trading gold on margin carries a high
            level of risk.
          </p>
        </div>
      </section>

      <MacroToolsStrip />
      <div className="pb-14" />
    </Layout>
  );
}

function MiniStat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/60 p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className={cn("mt-2 font-mono text-lg font-semibold", tone)}>{value}</div>
    </div>
  );
}

function PivotTable({ title, levels }: { title: string; levels: MarketStation["camarilla"] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border/60">
      <div className="border-b border-border/60 bg-card/80 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {title}
      </div>
      <table className="w-full text-sm">
        <tbody className="font-mono">
          {levels.map((l) => (
            <tr key={l.tag} className="border-t border-border/40 first:border-t-0">
              <td
                className={cn(
                  "px-4 py-2 text-xs uppercase tracking-wider",
                  l.kind === "resistance"
                    ? "text-destructive"
                    : l.kind === "support"
                      ? "text-emerald"
                      : "text-gold",
                )}
              >
                {l.tag}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">${l.value.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventRow({ event }: { event: CalendarEvent }) {
  const d = new Date(event.date);
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-card/60 px-4 py-3">
      <div>
        <div className="text-sm font-semibold">{event.title}</div>
        <div className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          {d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })} ·{" "}
          {d.toISOString().slice(11, 16)} UTC
        </div>
      </div>
      <div className="text-right font-mono text-[11px] text-muted-foreground">
        {event.forecast && <div>Forecast {event.forecast}</div>}
        {event.previous && <div>Previous {event.previous}</div>}
      </div>
    </div>
  );
}

function MacroCard({
  title,
  line,
  href,
  linkLabel,
}: {
  title: string;
  line: string | null | undefined;
  href: string;
  linkLabel: string;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/60 p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{title}</div>
      <p className="mt-2 text-sm text-muted-foreground">{line ?? "Latest official reading is being refreshed."}</p>
      <Link to={href} className="mt-3 inline-flex items-center gap-1 text-sm text-gold hover:underline">
        {linkLabel} <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
